import React from 'react';
import { Pressable, ScrollView, StyleSheet, View } from 'react-native';
import Button from 'shared/components/vxl/Button/Button';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import ListItem from 'shared/components/vxl/ListItem/ListItem';
import Checkbox from 'shared/components/vxl/selection/Checkbox/Checkbox';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import BodySmText from 'shared/components/vxl/typography/primary/body/BodySmText';
import TitleLgText from 'shared/components/vxl/typography/primary/title/TitleLgText';
import { isAndroid } from 'shared/constants/platform';
import { VXLComponentId } from 'shared/renderer/types';
import theme from 'shared/styles/theme';

export type LinkAccountButtonConfig = {
  analyticsId: string;
  customAttributes?: { cta: string };
  isDisabled: boolean;
  isLoading: boolean;
  label: string;
  onPress: () => void;
  type: 'PRIMARY' | 'SECONDARY';
};

export type LinkAccountScreenContent = {
  bullets?: string[];
  description?: string;
  subContent?: string;
  termsAndConditionsCopy?: string;
  title?: string;
};

type LinkAccountScreenContentSectionProps = {
  contentBullets: string[];
  onToggleAgreement: () => void;
  screenContent: LinkAccountScreenContent | undefined;
  userAgreementChecked: boolean;
};

type LinkAccountFooterProps = {
  bottomInset: number;
  primaryButton: LinkAccountButtonConfig;
  secondaryButton: LinkAccountButtonConfig | null;
};

export const LinkAccountScreenContentSection = ({
  contentBullets,
  onToggleAgreement,
  screenContent,
  userAgreementChecked,
}: LinkAccountScreenContentSectionProps) => (
  <ScrollView contentContainerStyle={[styles.scrollContentContainer]}>
    <View>
      <TitleLgText
        style={[styles.contentContainer, styles.marginBottom]}
        numberOfLines={0}
      >
        {screenContent?.title}
      </TitleLgText>
      <BodyBaseText
        style={[styles.contentContainer, styles.marginBottom]}
        numberOfLines={0}
      >
        {screenContent?.description}
      </BodyBaseText>
      <View>
        {contentBullets.map((bullet, index) => (
          <View key={`bullet-${bullet}`}>
            <ListItem
              primaryCopy={bullet}
              leadingAccessory={{
                id: `LIGHTNING_BULLET_${index + 1}`,
                name: VXLComponentId.ICON_AVATAR,
                props: {
                  iconKey: 'SUCCESS',
                },
              }}
              showsChevron={false}
            />
            {index < contentBullets.length - 1 ? (
              <View style={styles.contentContainer}>
                <HairLineDivider />
              </View>
            ) : null}
          </View>
        ))}
      </View>

      <View style={styles.tAndC}>
        <Checkbox
          analyticsId="agreementCheckbox"
          checked={userAgreementChecked}
          onSelectedStateChange={onToggleAgreement}
        />
        <Pressable style={styles.termsPressable} onPress={onToggleAgreement}>
          <View style={styles.termsAndCondition}>
            <BodyBaseText numberOfLines={4}>
              {screenContent?.termsAndConditionsCopy}
            </BodyBaseText>
          </View>
        </Pressable>
      </View>
    </View>
    <BodySmText style={styles.contentContainer} numberOfLines={3}>
      {screenContent?.subContent}
    </BodySmText>
  </ScrollView>
);

export const LinkAccountFooter = ({
  bottomInset,
  primaryButton,
  secondaryButton,
}: LinkAccountFooterProps) => (
  <View
    style={[
      styles.footerContainer,
      {
        paddingBottom: isAndroid
          ? bottomInset + theme.spacing.m
          : bottomInset + theme.spacing.l,
      },
    ]}
  >
    <Button
      label={primaryButton.label}
      onPress={primaryButton.onPress}
      isDisabled={primaryButton.isDisabled}
      isLoading={primaryButton.isLoading}
      analyticsId={primaryButton.analyticsId}
      customAttributes={primaryButton.customAttributes}
      size="LARGE"
      type={primaryButton.type}
      width="FULL"
    />
    {secondaryButton ? (
      <Button
        label={secondaryButton.label}
        onPress={secondaryButton.onPress}
        isDisabled={secondaryButton.isDisabled}
        isLoading={secondaryButton.isLoading}
        analyticsId={secondaryButton.analyticsId}
        customAttributes={secondaryButton.customAttributes}
        size="LARGE"
        type={secondaryButton.type}
        width="FULL"
      />
    ) : null}
  </View>
);

const styles = StyleSheet.create({
  contentContainer: {
    paddingHorizontal: theme.spacing.m,
  },
  footerContainer: {
    borderTopColor: theme.color.border.primarySolid,
    borderTopWidth: 1,
    gap: theme.spacing.m,
    padding: theme.spacing.m,
  },
  marginBottom: {
    marginBottom: theme.spacing.m,
  },
  scrollContentContainer: {
    flex: 1,
    gap: theme.spacing.m,
    justifyContent: 'space-between',
    paddingVertical: theme.spacing.m,
  },
  tAndC: {
    alignItems: 'flex-start',
    flexDirection: 'row',
    gap: theme.spacing.m,
    paddingHorizontal: theme.spacing.m,
  },
  termsAndCondition: {
    flexShrink: 1,
    paddingVertical: theme.spacing.s,
  },
  termsPressable: {
    flex: 1,
  },
});
